import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {DisabledText} from 'src/components/profile-tab-structure';
import {Skeleton} from 'src/components/skeleton';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {selectUserShortInfo} from 'src/modules/main/services/user-info-slice';
import {
    clearAccountTeam,
    selectAccountDetails,
    setAccountTeam,
} from 'src/modules/profile/account/services/account-page-slice';
import {getAccountManagerInfo} from 'src/openapi/admin/apis/AccountManagerRelationsApi';
import {getManagedAccounts} from 'src/openapi/admin/apis/GetManagedAccountsApi';
import {useAppDispatch} from 'src/store/hooks';

import Button, {FlatButton} from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';

import {AccountManager} from './account-manager';
import {AccountManagersSectionSkeleton} from './account-managers-section-skeleton';
import {AccountTeamMemberEdit} from './account-team-mambers-edit';

type Props = {
    accountId: number,
};

export const AccountManagersSection: React.FC<Props> = ({accountId}) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const me = useSelector(selectUserShortInfo);
    const {accountManagers} = useSelector(selectAccountDetails);
    const {accountSubmit} = useEventTracking();

    const [shownEditModal, setShownEditModal] = useState(false);
    const {canEditAccount} = useAdminAccessRight();

    useEffect(() => {
        if (!accountManagers) {
            apiDispatch(getAccountManagerInfo({accountId})).then(response => {
                if (response.payload.success) {
                    dispatch(setAccountTeam(response.payload.data));
                }
            });
        }
    }, [accountManagers, accountId, apiDispatch, dispatch]);

    const onOpenEditModal = useCallback(() => {
        setShownEditModal(true);
    }, []);

    const onCloseEditModal = useCallback(() => {
        accountSubmit({place: 'Edit Account Team', submit: false});
        setShownEditModal(false);
    }, [accountSubmit]);

    const onSuccessEdit = useCallback(() => {
        accountSubmit({place: 'Edit Account Team', submit: true});
        dispatch(clearAccountTeam());
        if (me) {
            apiDispatch(getManagedAccounts({accountManagerId: me.userId}));
        }
        setShownEditModal(false);
    }, [accountSubmit, apiDispatch, dispatch, me]);

    return (
        <ErrorBoundary>
            <Skeleton view={<AccountManagersSectionSkeleton/>} shown={!accountManagers}>
                <FormSection
                    headline="Account Team"
                    info={headline => (
                        <Item grow={1}>
                            <Group justify="space-between" alignItems="center">
                                {headline}
                                {!!accountManagers?.length && canEditAccount(accountId) &&
                                    <FlatButton onClick={onOpenEditModal}><b>Edit</b></FlatButton>}
                            </Group>
                        </Item>
                    )}>
                    <Group direction="v" gap="m" alignItems="start">
                        {accountManagers ? accountManagers.map(accountManager => (
                            <AccountManager key={accountManager.id} accountManager={accountManager}/>
                        )) : null}
                        {accountManagers && !accountManagers?.length ? (
                            <Item grow={1}>
                                <Group justify="space-between">
                                    <DisabledText>This account has no account team members</DisabledText>
                                    <Button variant="Type.PRIMARY" onClick={onOpenEditModal}>Add</Button>
                                </Group>
                            </Item>
                        ) : null}
                    </Group>
                </FormSection>
            </Skeleton>
            <AccountTeamMemberEdit
                shown={shownEditModal}
                onClose={onCloseEditModal}
                accountId={accountId}
                onSuccess={onSuccessEdit}
            />
        </ErrorBoundary>
    );
};
